export const landing6 = {
  hero: {
    badge: "Bespire Pro — For Organizations",
    title: "Turn your professional\ncommunity into a marketplace.",
    subtitle: "Expert matching + training courses + digital content — on a single, branded platform.",
    features: [
      "List and match experts with clients",
      "Sell training courses (online/offline)",
      "Sell digital content (videos, materials, packages)",
      "AI-powered operations with minimal admin",
    ],
    ctaDemo: "Book a 20-minute overview",
    ctaModel: "See how the model works",
    microcopy: "Not a sales call. We show you the model and see if it fits.",
  },
  painPoints: {
    badge: "Why it's worth it",
    title: "What Bespire\nchanges instantly.",
    items: [
      {
        icon: "rocket",
        title: "Your website sells, not just informs",
        description: "Booking, registration, payment — all in one place, on your platform. Prospects become clients instantly.",
      },
      {
        icon: "zap",
        title: "Training organization takes minutes, not hours",
        description: "Automatic confirmations, reminders, seat tracking — you focus only on the content.",
      },
      {
        icon: "search",
        title: "Your experts are found — on your site",
        description: "Searchable, filterable profiles on your own platform. Prospects book with you, not on Google.",
      },
      {
        icon: "package",
        title: "Your knowledge becomes a product",
        description: "Trainings, methodologies, videos — in a sellable online format, 24/7, automatically.",
      },
    ],
  },
  solution: {
    badge: "The solution",
    title: "Much more than\njust a website.",
    pillars: [
      {
        icon: "users",
        title: "Expert marketplace",
        subtitle: "Prospects find your experts on your platform — not on Google.",
        features: [
          "All experts in one place with searchable profiles",
          "Visitors filter by specialty, location, and availability",
          "One click from inquiry to booking — no unnecessary runaround",
          "Fully customizable design — tailored to your organization's branding",
        ],
      },
      {
        icon: "graduationCap",
        title: "Training sales",
        subtitle: "You don't just announce courses — you sell them. Registration, payment, and confirmation run automatically.",
        features: [
          "Every course gets its own sales page automatically",
          "Registrants pay online and get instant confirmation",
          "Reminders go out before the training — no manual work",
          "Seat counter, discounted pricing, and coupon codes built in",
        ],
      },
      {
        icon: "fileVideo",
        title: "Digital content",
        subtitle: "Your videos, materials, and methodologies finally become sellable products.",
        features: [
          "Videos and downloadable materials in one place",
          "Bundles by target audience (e.g. parents, professionals)",
          "One-time or subscription purchase",
          "Purchased content becomes available in the user's profile",
        ],
      },
      {
        icon: "sparkles",
        title: "AI that works for you",
        subtitle: "Website, calendar, marketing — the AI assistant handles operations so you can focus on content.",
        features: [
          "AI builds your website in minutes — logo, colors, content automatically",
          "Clients book trainings and consultations 24/7, with reminders",
          "Social media posts, email campaigns — no marketing expertise needed",
          "Ask questions, AI answers and executes — like a personal assistant",
        ],
      },
    ],
  },
  benefits: {
    badge: "Benefits",
    highlightBadge: "For You ★",
    title: "Why does it work better\nas a marketplace?",
    columns: [
      {
        icon: "heart",
        title: "For clients",
        subtitle: "(parents, clients, prospects)",
        items: [
          "Faster decisions: transparent expert offering",
          "Trust: organization's brand + certification logic",
          "Easy next step: inquiry, booking, purchase in one place",
          "All experts in one place with verified profiles",
        ],
      },
      {
        icon: "briefcase",
        title: "For experts",
        subtitle: "",
        items: [
          "More inquiries and higher quality leads",
          "Professional presence under the organization's umbrella",
          "Less admin, cleaner processes",
          "Focus on expertise, not on marketing",
        ],
      },
      {
        icon: "building",
        title: "For the organization",
        subtitle: "(you)",
        items: [
          "New revenue layer beyond training (referrals, packages, digital products)",
          "Brand control: on your own platform, not a third-party marketplace",
          "Measurability: what brings leads, what sells, where users drop off",
          "Scalability: more cities, more categories — same system",
        ],
      },
    ],
  },
  whyWorthIt: {
    badge: "Why it's worth it",
    title: "6 reasons Bespire\nis worth the switch.",
    items: [
      {
        icon: "zap",
        title: "Live in days, not months",
        description: "No months-long development — the platform goes live in days, with our expert team guiding you.",
      },
      {
        icon: "bot",
        title: "AI-powered operations",
        description: "Content generation, communication automation, admin tasks — the AI assistant helps everywhere.",
      },
      {
        icon: "shield",
        title: "Under your own brand",
        description: "You're not on a third-party marketplace — your brand, your domain, your platform.",
      },
      {
        icon: "globe",
        title: "Multilingual system",
        description: "Whether you're expanding or already operating in multiple languages, no problem — the system supports all European languages.",
      },
      {
        icon: "repeat",
        title: "Upsell & cross-sell automation",
        description: "Training → expert → digital content (and vice versa): the system automatically recommends and sells.",
      },
      {
        icon: "barChart",
        title: "Measurable ROI",
        description: "See exactly what brings leads, what sells, where users drop off — data-driven decisions.",
      },
    ],
  },
  proPackage: {
    title: "Everything a training\ncenter needs.",
    description: "Custom pricing, full-featured platform — tailored to your needs.",
    badge: "For Training Centers",
    price: "Custom pricing",
    period: "tailored to your needs",
    cardDescription: "Marketplace, training sales, and full organizational operations — on one platform.",
    features: [
      "Full website creation",
      "AI-powered booking system",
      "Digital product sales",
      "Online course sales",
      "Individual or group session sales",
      "Group workshop organization",
      "Passes and memberships management",
      "AI-generated marketing content",
      "Email marketing automation",
      "Email subscription forms",
      "Custom domain",
      "Multi-user admin panel",
      "Unlimited team member management",
      "Fully custom pricing",
      "Dedicated account manager",
      "24/7 priority support",
      "AI assistant for daily work",
      "Custom API integration",
      "White-label solution",
      "Custom onboarding and training",
      "SLA guarantee",
      "Full GDPR compliance",
      "Prioritized feature requests",
    ],
    cta: "Request a quote",
  },
  roiCalculator: {
    badge: "ROI calculator",
    title: "How much extra revenue can you expect?",
    subtitle: "Model in 30 seconds what a Bespire Pro marketplace could bring your organization.",
    expertsLabel: "How many active experts do you have?",
    revenueLabel: "Average monthly service revenue per expert",
    expertsUnit: "experts",
    revenueUnit: "HUF / mo",
    output: {
      trainings: "training sales / mo (estimated)",
      digital: "digital products / mo (estimated)",
      bookings: "brokered bookings / mo (estimated)",
      total: "extra revenue per month",
    },
    disclaimer: "This is a model based on industry averages. We'll show you exact numbers in a 20-minute consultation.",
    ctaExact: "Get an exact quote",
  },
  caseStudy: {
    badge: "Case study",
    title: "How does an organization work with Bespire Pro?",
    subtitle: "A typical professional organization's 6-month results — anonymized example.",
    sample: {
      org: {
        label: "Organization",
        value: "National Professional Association • Healthcare • 150 experts",
      },
      challenge: {
        label: "Challenge",
        value: "Online transition, coordinating 150 experts, ensuring a unified client experience.",
      },
      solution: {
        label: "Solution",
        value: "Bespire Pro marketplace + training management + digital content sales on one platform.",
      },
      result: {
        label: "Result (after 6 months)",
        bullets: [
          "+340 training sales",
          "+62 new active experts onboarded",
          "−68% admin time",
          "+HUF 12,200,000 new revenue",
        ],
      },
    },
    expand: "Show details",
    ctaFull: "Read the full case study",
  },
  howItWorks: {
    badge: "How it works",
    title: "You launch in 6 steps.",
    steps: [
      {
        number: "01",
        title: "Application",
        description: "Fill out the short inquiry form and we'll get in touch within 24 hours.",
        timeframe: "Reply within 24 hours",
      },
      {
        number: "02",
        title: "Meeting",
        description: "We discuss your needs, goals, and ideas in a short online consultation.",
        timeframe: "30 min online",
      },
      {
        number: "03",
        title: "Quote",
        description: "We send a personalized offer detailing exactly what you get and for how much.",
        timeframe: "3 business days",
      },
      {
        number: "04",
        title: "Preparation",
        description: "We build your site and set up the entire system — you don't have to lift a finger.",
        timeframe: "2–4 weeks",
      },
      {
        number: "05",
        title: "Handover",
        description: "We present the finished system, train you, and hand over all access.",
        timeframe: "1 week of training",
      },
      {
        number: "06",
        title: "Launch",
        description: "We take the site live — and you welcome your first clients!",
        timeframe: "go-live",
      },
    ],
    totalTime: "On average 4–6 weeks from application to launch.",
  },
  trustStrip: {
    label: "Behind Bespire Pro:",
    items: [
      "Hungarian development team",
      "Full GDPR compliance",
      "EU-based servers",
      "99.9% SLA system guarantee",
    ],
  },
  faq: {
    title: "Frequently asked questions",
    questions: [
      {
        q: "Who is this solution for?",
        a: "Organizations that provide training, work with certified experts, and want transparent, revenue-generating marketplace operations — e.g., methodological organizations, professional associations, training centers.",
      },
      {
        q: "What's the difference from a regular website?",
        a: "A website informs. Bespire Pro does business: it connects supply and demand and simplifies the next step (inquiry/booking/purchase).",
      },
      {
        q: "Can I have multiple target groups? (e.g., parents + professionals)",
        a: "Yes — with separate messaging, separate pages, separate entry points. The system supports multiple target group management.",
      },
      {
        q: "How much does it cost?",
        a: "The Pro package has custom pricing based on the organization's size and needs. Request a 20-minute overview and we'll provide a personalized offer.",
      },
      {
        q: "How quickly can it go live?",
        a: "After building the structure and uploading content, the system typically goes live in days — not months.",
      },
      {
        q: "Can it run on our own domain?",
        a: "Of course — the platform runs entirely under your own brand and domain, users won't see any Bespire branding.",
      },
      {
        q: "Which systems can we migrate our data from?",
        a: "We support the most common export formats (CSV, Excel, JSON). The migration assistant guides you through the import, and the initial data migration is part of the Pro package — at no extra cost.",
      },
      {
        q: "Will we have a dedicated project manager?",
        a: "Yes, the Pro package includes a named project manager through launch and during the first 3 months of onboarding. You talk to one person, not a ticket system.",
      },
      {
        q: "How many hours of onboarding will our team get?",
        a: "6–10 hours of structured training per role (admin, expert, marketing) with recorded video materials so new joiners can ramp up quickly.",
      },
      {
        q: "What contractual guarantees are there?",
        a: "99.9% SLA system guarantee, GDPR-compliant data processing agreement, exit clause and full data export available at any time. All in writing, on record.",
      },
      {
        q: "Can we pay by bank transfer / monthly or annual invoice?",
        a: "Yes, bank transfer, EU-compliant invoice, and a choice of monthly or annual billing cycle. We offer a discount for annual billing.",
      },
    ],
  },
  closingCta: {
    title: "Want your organization to\noperate as a marketplace?",
    description: "I'll show you a sample structure tailored to your operations: experts + training + digital content.",
    ctaDemo: "Request a 20-minute overview",
    ctaFree: "Get a recommended structure (free)",
    microcopy: "We respond to quote requests within 1 business day. The 20-minute consultation is free and not a sales call.",
  },
  animations: {
    marketplace: {
      searchText: "therapist",
      filters: ["Budapest", "Available online"],
      experts: [
        { name: "Dr. Anna Nagy", spec: "Therapist", city: "Budapest" },
        { name: "Péter Kiss", spec: "Coach", city: "Online" },
        { name: "Eszter Tóth", spec: "Consultant", city: "Debrecen" },
      ],
      bookButton: "Book now",
    },
    course: {
      popular: "Popular",
      spots: "spots",
      title: "Foundation Training 2026",
      date: "Mar 15–17, 2026",
      location: "Budapest · 3-day intensive",
      price: "€299",
      ctaButton: "Register →",
      ctaDone: "✓ Registered",
      paymentLabel: "Payment",
      cardNumber: "•••• •••• •••• 4242",
      totalLabel: "Total:",
      payButton: "Pay →",
      successTitle: "Registration successful!",
      successSubtitle: "Confirmation sent to your email.",
    },
    digitalContent: {
      sectionTitle: "Digital content",
      stats: "42 videos · 18.5 hours",
      items: [
        { title: "Methodology basics", meta: "12 videos" },
        { title: "Practical guide", meta: "8 videos" },
        { title: "Advanced techniques", meta: "6 videos" },
        { title: "Parents package", meta: "16 videos" },
      ],
      price: "€19.90",
      buyButton: "Buy now →",
      successTitle: "Purchase successful!",
      successSubtitle: "Available in your profile",
    },
    ai: {
      userMessage: "Display Eszter Farkas's profile on the site!",
      websiteReply: "Eszter Farkas's profile is live! 🎉",
      bookingsTitle: "3 new bookings today",
      bookings: [
        { name: "Petra Kiss", time: "10:00", status: "✓" },
        { name: "Tamás Nagy", time: "14:30", status: "✓" },
        { name: "Anna Tóth", time: "16:00", status: "⏳" },
      ],
      emailCampaign: "Email campaign sent to 247 recipients",
      socialPost: "Social post",
    },
  },
  contactForm: {
    badge: "Contact",
    title: "Let's discuss the possibilities.",
    subtitle: "Fill out the form and we'll get back to you within 24 hours.",
    fields: {
      orgName: "Organization name",
      contactPerson: "Contact person",
      email: "Email address",
      phone: "Phone number",
      website: "Current website URL",
      expertCount: "How many experts do you work with?",
      services: "Which services are you interested in?",
      message: "Message",
    },
    placeholders: {
      orgName: "e.g. National Coaching Association",
      contactPerson: "e.g. Anna Smith",
      email: "e.g. info@organization.com",
      phone: "e.g. +1 555 123 4567",
      website: "e.g. www.organization.com",
      expertCount: "Select...",
      services: "e.g. Marketplace, Training, Digital content",
      message: "How can we help? Describe your needs...",
    },
    expertCounts: ["1–100", "100–1,000", "1,000+"],
    submitButton: "Send",
    submitting: "Sending...",
    successTitle: "Message sent!",
    successDescription: "We'll get back to you shortly.",
    sla: "We respond to quote requests within 1 business day. The 20-minute consultation is free and not a sales call.",
    consultation: {
      title: "Book a consultation",
      description: "Schedule a free 30-minute consultation to discuss tailored solutions for your organization.",
      calendarPlaceholder: "Coming soon",
      availability: "Mon–Fri, 9:00–17:00",
    },
  },
};
